import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

/**
 * CareLogix™ ORACLE — Core-Satellite Intelligence™ (CSIA™) — Phase 38
 *
 * Public user-facing page. Three sections:
 *   1. What is CSIA — explainer with orbital animation
 *   2. My Core — always-active 8 intelligence layers
 *   3. My Satellites — tier-gated activation surface
 *
 * Truthfulness: data flows from /api/oracle/core-satellite/manifest + /my.
 * No fabricated activation, no synthetic telemetry.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api/oracle/core-satellite`;

// CareLogix theme — emerald · gold · black · ivory
const C = {
  bg:       "#0a1a14",
  bg2:      "#0f2820",
  emerald:  "#10b981",
  emeraldD: "#047857",
  gold:     "#facc15",
  goldD:    "#ca8a04",
  ivory:    "#f5f5dc",
  ivoryD:   "#d4d4aa",
  border:   "rgba(16, 185, 129, 0.25)",
};

const S = {
  page: {
    minHeight: "100vh",
    background: `radial-gradient(circle at 50% 0%, ${C.bg2} 0%, ${C.bg} 60%)`,
    color: C.ivory,
    padding: "32px 24px 80px",
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  inner: { maxWidth: 1200, margin: "0 auto" },
  h1: { fontFamily: "'Cinzel', serif", color: C.gold, fontSize: "clamp(28px, 4vw, 44px)", margin: 0, letterSpacing: 1 },
  kicker: { textTransform: "uppercase", color: C.emerald, fontSize: 12, letterSpacing: 2, fontWeight: 700 },
  tagline: { color: C.ivoryD, fontSize: 14, marginTop: 10, fontStyle: "italic" },
  section: { marginTop: 48, paddingTop: 24, borderTop: `1px solid ${C.border}` },
  h2: { color: C.gold, fontSize: 20, margin: "0 0 12px", letterSpacing: 1 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 },
  card: {
    background: "rgba(16, 185, 129, 0.04)",
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: "12px 14px",
    minHeight: 88,
    display: "flex", flexDirection: "column", justifyContent: "space-between",
  },
  cardLabel: { color: C.ivory, fontWeight: 600, fontSize: 14 },
  cardTagline: { color: C.ivoryD, fontSize: 11, marginTop: 4 },
  cardStatus: { color: C.emerald, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" },
  btn: {
    background: C.gold, color: "#0a0a0a", border: "none", padding: "6px 12px",
    borderRadius: 4, fontSize: 11, fontWeight: 700, cursor: "pointer", letterSpacing: 0.5,
  },
  btnGhost: {
    background: "transparent", color: C.ivory,
    border: `1px solid ${C.border}`, padding: "6px 12px",
    borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: "pointer",
  },
  pill: {
    display: "inline-block", padding: "2px 8px", borderRadius: 999,
    fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
  },
  disclaimer: { color: C.ivoryD, fontSize: 11, marginTop: 40, textAlign: "center", opacity: 0.7 },
  err: { color: "#fca5a5", padding: "10px 14px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6, marginTop: 12 },
};

function OrbitVisualization() {
  // CSS / SVG orbital ring — emerald core + gold satellites rotating
  const satellites = 6;
  return (
    <div
      data-testid="csia-orbit"
      style={{ position: "relative", width: "100%", maxWidth: 480, aspectRatio: "1/1", margin: "16px auto 0" }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 400 400" width="100%" height="100%">
        <defs>
          <radialGradient id="csia-core-grad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={C.gold} stopOpacity={0.95} />
            <stop offset="60%" stopColor={C.emerald} stopOpacity={0.55} />
            <stop offset="100%" stopColor={C.emeraldD} stopOpacity={0.1} />
          </radialGradient>
        </defs>
        {/* Core */}
        <circle cx="200" cy="200" r="88" fill="url(#csia-core-grad)" stroke={C.gold} strokeWidth="1.5" />
        <text x="200" y="195" textAnchor="middle" fill="#0a0a0a" fontFamily="'Cinzel', serif" fontWeight="700" fontSize="14">CORE</text>
        <text x="200" y="214" textAnchor="middle" fill="#0a0a0a" fontFamily="'Inter', sans-serif" fontWeight="600" fontSize="11">70-80%</text>
        {/* Orbit ring */}
        <circle cx="200" cy="200" r="160" fill="none" stroke={C.border} strokeWidth="1" strokeDasharray="2 4" />
        {/* Satellite dots — rotating */}
        <g style={{ transformOrigin: "200px 200px", animation: "csia-orbit-spin 36s linear infinite" }}>
          {Array.from({ length: satellites }).map((_, i) => {
            const angle = (i / satellites) * Math.PI * 2;
            const cx = 200 + Math.cos(angle) * 160;
            const cy = 200 + Math.sin(angle) * 160;
            return <circle key={i} cx={cx} cy={cy} r="10" fill={C.gold} stroke={C.emerald} strokeWidth="2" />;
          })}
        </g>
      </svg>
      <style>{`@keyframes csia-orbit-spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
    </div>
  );
}

export default function OracleCoreSatellitePage() {
  const [manifest, setManifest] = useState(null);
  const [my, setMy]             = useState(null);
  const [loading, setLoading]   = useState(true);
  const [busy, setBusy]         = useState("");
  const [err, setErr]           = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const [m, u] = await Promise.all([
        axios.get(`${API}/manifest`),
        axios.get(`${API}/my`),
      ]);
      setManifest(m.data);
      setMy(u.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "load_failed");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onActivate = useCallback(async (key) => {
    setBusy(key); setErr("");
    try {
      await axios.post(`${API}/my/activate`, { satellite_key: key });
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "SATELLITE_QUOTA_EXCEEDED") {
        setErr(`${d.error} — used ${d.quota_used}/${d.quota_total}. ${d.next_action}`);
      } else {
        setErr(String(d || e?.message || "activate_failed"));
      }
    } finally { setBusy(""); }
  }, [load]);

  const onDeactivate = useCallback(async (key) => {
    setBusy(key); setErr("");
    try {
      await axios.post(`${API}/my/deactivate`, { satellite_key: key });
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "deactivate_failed");
    } finally { setBusy(""); }
  }, [load]);

  const satellitesByCluster = useMemo(() => {
    if (!manifest) return {};
    return manifest.satellites || {};
  }, [manifest]);

  return (
    <main style={S.page} data-testid="oracle-core-satellite-page">
      <div style={S.inner}>
        <div style={S.kicker}>CareLogix ORACLE™</div>
        <h1 style={S.h1}>Core-Satellite Intelligence™</h1>
        <div style={S.tagline}>Stable Core. Intelligent Satellites. Infinite Expansion.</div>

        {/* Section 1 — Explainer + orbital animation */}
        <section style={S.section} data-testid="csia-section-explainer">
          <h2 style={S.h2}>What Is Core-Satellite Intelligence™?</h2>
          <p style={{ color: C.ivoryD, fontSize: 14, lineHeight: 1.6, maxWidth: 800 }}>
            CSIA™ is CareLogix's platform-wide intelligence architecture. The <strong>Core</strong> represents
            the always-on foundation every user receives — LOGIX-2™ reasoning, LifeScore™, FoodRx™,
            CareThreads™, the Core Knowledge Engine™, and our constitutional Trust &amp; Safety layer.
            Orbiting the Core, <strong>Satellites</strong> are specialised intelligence modules — Healthcare,
            Executive, Capital, and future domains — that activate on demand. Every satellite routes
            through the Core Knowledge Engine™ → LOGIX-2™ → you, so context, governance, and truthfulness
            are guaranteed end-to-end.
          </p>
          <OrbitVisualization />
        </section>

        {err && <div role="alert" style={S.err} data-testid="csia-error">{err}</div>}

        {/* Section 2 — My Core */}
        <section style={S.section} data-testid="csia-section-core">
          <h2 style={S.h2}>My Core</h2>
          <div style={{ color: C.ivoryD, fontSize: 12, marginBottom: 12 }}>
            8 always-active intelligence layers. Tier: <strong style={{ color: C.gold }}>{loading ? "—" : (my?.tier || "free").toUpperCase()}</strong>
          </div>
          <div style={S.grid}>
            {(manifest?.core_layers || []).map((c) => (
              <div key={c.key} style={S.card} data-testid={`csia-core-${c.key}`}>
                <div>
                  <div style={S.cardLabel}>{c.label}</div>
                  <div style={S.cardTagline}>{c.tagline}</div>
                </div>
                <div style={S.cardStatus}>● ACTIVE</div>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3 — My Satellites */}
        <section style={S.section} data-testid="csia-section-satellites">
          <h2 style={S.h2}>My Satellites</h2>
          <div style={{ color: C.ivoryD, fontSize: 12, marginBottom: 12 }}>
            {loading ? "Loading…" : (
              <>Quota: <strong style={{ color: C.gold }}>{my?.quota_used ?? 0} / {my?.quota_total ?? 0}</strong> active</>
            )}
          </div>

          {(my?.satellites_activated || []).length > 0 && (
            <>
              <div style={{ color: C.emerald, fontSize: 11, marginBottom: 8, letterSpacing: 1, textTransform: "uppercase" }}>● Active</div>
              <div style={S.grid} data-testid="csia-active-satellites">
                {(my.satellites_activated || []).map((s) => (
                  <div key={s.key} style={S.card} data-testid={`csia-sat-active-${s.key}`}>
                    <div>
                      <div style={S.cardLabel}>{s.label}</div>
                      <div style={S.cardTagline}>{s.cluster} · {s.description}</div>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                      <span style={{ ...S.pill, background: "rgba(16,185,129,0.15)", color: C.emerald }}>ACTIVE</span>
                      <button
                        type="button"
                        onClick={() => onDeactivate(s.key)}
                        disabled={busy === s.key}
                        style={S.btnGhost}
                        data-testid={`csia-deactivate-${s.key}`}
                      >Deactivate</button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {Object.entries(satellitesByCluster).map(([cluster, items]) => {
            const remaining = items.filter((s) => !(my?.satellites_activated || []).some((a) => a.key === s.key));
            if (remaining.length === 0) return null;
            return (
              <div key={cluster} style={{ marginTop: 22 }}>
                <div style={{ color: C.gold, fontSize: 11, marginBottom: 8, letterSpacing: 1, textTransform: "uppercase" }}>{cluster}</div>
                <div style={S.grid}>
                  {remaining.map((s) => (
                    <div key={s.key} style={S.card} data-testid={`csia-sat-available-${s.key}`}>
                      <div>
                        <div style={S.cardLabel}>{s.label}</div>
                        <div style={S.cardTagline}>{s.description}</div>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                        <span style={{ ...S.pill, background: "rgba(245,245,220,0.06)", color: C.ivoryD }}>AVAILABLE</span>
                        <button
                          type="button"
                          onClick={() => onActivate(s.key)}
                          disabled={busy === s.key}
                          style={S.btn}
                          data-testid={`csia-activate-${s.key}`}
                        >Activate</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </section>

        <div style={S.disclaimer}>
          Educational intelligence only. Not medical advice. Not financial advice. Routes through
          the Core Knowledge Engine™ → LOGIX-2™ → you. CareLogix™ ORACLE — Multi-Domain Intelligence Command™.
        </div>
      </div>
    </main>
  );
}
